import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { saveUser, getUsers, deleteUser, updateUserPermissions, resetUserPassword } from '../firebase/userService';
import './UserManagement.css';

function UserManagement() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    initials: '',
    role: 'Varsity',
    permissions: {
      sessionHost: false,
      pointTracking: false
    }
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    const result = await getUsers();
    if (result.success) {
      setUsers(result.users);
    }
    setLoading(false);
  };

  const handleSubmitUser = async (e) => {
    e.preventDefault();

    if (!userForm.name || !userForm.email) {
      alert('Please fill in all required fields');
      return;
    }

    // Generate initials if not provided
    const initials = userForm.initials ||
      userForm.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

    const result = await saveUser({
      ...userForm,
      initials,
      id: editingUser?.id
    });

    if (result.success) {
      if (!editingUser) {
        // New user created - show default password
        alert(`User created successfully!\n\nDefault Password: Debate212\n\nPlease share this password with ${userForm.name}. They will be able to change it after logging in.`);
      } else {
        alert('User updated successfully!');
      }

      setShowUserModal(false);
      setEditingUser(null);
      setUserForm({
        name: '',
        email: '',
        initials: '',
        role: 'Varsity',
        permissions: {
          sessionHost: false,
          pointTracking: false
        }
      });
      await loadUsers();
    } else {
      alert('Error saving user. Please try again.');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      name: user.name,
      email: user.email,
      initials: user.initials || '',
      role: user.role || 'Varsity',
      permissions: user.permissions || {
        sessionHost: false,
        pointTracking: false
      }
    });
    setShowUserModal(true);
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user? This will remove them from all associated records.')) {
      const result = await deleteUser(userId);
      if (result.success) {
        await loadUsers();
      } else {
        alert('Error deleting user. Please try again.');
      }
    }
  };

  const handlePermissionToggle = async (userId, permissionType, currentValue) => {
    const user = users.find(u => u.id === userId);
    const updatedPermissions = {
      ...user.permissions,
      [permissionType]: !currentValue
    };

    const result = await updateUserPermissions(userId, updatedPermissions);
    if (result.success) {
      await loadUsers();
    }
  };

  const handleResetPassword = async (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;

    if (window.confirm(`Reset password for ${user.name}?\n\nThis will:\n1. Reset their password to "Debate212"\n2. Delete their existing authentication (if they've logged in before)\n3. Require them to change password on next login\n\nContinue?`)) {
      try {
        // Update Firestore and delete Firebase Auth account automatically
        const result = await resetUserPassword(userId);

        if (result.success) {
          const statusMessage = result.authDeleted
            ? 'Authentication account was deleted. User can now log in with the default password.'
            : 'User has not logged in before. They can log in with the default password.';

          alert(`Password reset successful!\n\nUser: ${user.name}\nEmail: ${user.email}\nDefault Password: Debate212\n\nStatus: ${statusMessage}\n\nPlease share these credentials with the user. They will be required to change their password after logging in.`);
          await loadUsers();
        } else if (result.warning) {
          alert(`Password reset with warning:\n\n${result.warning}\n\nUser: ${user.name}\nEmail: ${user.email}\nPassword: Debate212\n\nYou may need to manually run:\nnode resetAuthPassword.js ${user.email}`);
          await loadUsers();
        } else {
          alert('Error resetting password. Please try again.');
        }
      } catch (error) {
        console.error('Error resetting password:', error);
        alert('Error resetting password. Please try again.');
      }
    }
  };

  // Admin access check
  const isAdmin = currentUser?.email?.toLowerCase() === 'kaseywilleby@gmail.com' ||
                  currentUser?.email?.toLowerCase() === 'mr.willeby@gmail.com' ||
                  users.find(u => u.email?.toLowerCase() === currentUser?.email?.toLowerCase() && u.role === 'Admin');

  if (!isAdmin) {
    return (
      <div className="user-management">
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="page-header">
        <div className="header-with-back">
          <button onClick={() => navigate('/')} className="back-button">
            ← Back to Dashboard
          </button>
          <div className="header-text">
            <h1>User Management</h1>
            <p>Manage officers and their access permissions</p>
          </div>
        </div>
      </div>

      <div className="users-header">
        <h2>Officers & Staff</h2>
        <button
          onClick={() => {
            setEditingUser(null);
            setUserForm({
              name: '',
              email: '',
              initials: '',
              role: 'Varsity',
              permissions: {
                sessionHost: false,
                pointTracking: false
              }
            });
            setShowUserModal(true);
          }}
          className="add-user-button"
        >
          + Add User
        </button>
      </div>

      {loading ? (
        <p>Loading users...</p>
      ) : (
        <div className="users-list">
          {users.length === 0 ? (
            <div className="no-users">
              <p>No users yet. Click "Add User" to create one.</p>
            </div>
          ) : (
            <div className="users-grid">
              {users.map(user => (
                <div key={user.id} className="user-card">
                  <div className="user-card-header">
                    <div className="user-avatar">
                      {user.initials || user.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                    </div>
                    <div className="user-info">
                      <h3>{user.name}</h3>
                      <p>{user.email}</p>
                      {user.role && (
                        <span className={`user-role-badge ${user.role.toLowerCase()}`}>
                          {user.role}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="user-permissions">
                    <h4>Permissions</h4>
                    <label className="permission-checkbox">
                      <input
                        type="checkbox"
                        checked={user.permissions?.sessionHost || false}
                        onChange={() => handlePermissionToggle(
                          user.id,
                          'sessionHost',
                          user.permissions?.sessionHost || false
                        )}
                      />
                      <span>Session Host</span>
                      <small>Can manage own availability and cancel own appointments</small>
                    </label>

                    <label className="permission-checkbox">
                      <input
                        type="checkbox"
                        checked={user.permissions?.pointTracking || false}
                        onChange={() => handlePermissionToggle(
                          user.id,
                          'pointTracking',
                          user.permissions?.pointTracking || false
                        )}
                      />
                      <span>Point Tracking</span>
                      <small>Can create, approve, and edit point entries</small>
                    </label>
                  </div>

                  <div className="user-actions">
                    <button onClick={() => handleEditUser(user)} className="edit-btn">
                      Edit
                    </button>
                    <button onClick={() => handleResetPassword(user.id)} className="reset-password-btn">
                      Reset Password
                    </button>
                    <button onClick={() => handleDeleteUser(user.id)} className="delete-btn">
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* User Modal */}
      {showUserModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="modal-close" onClick={() => setShowUserModal(false)}>×</button>
            <h2>{editingUser ? 'Edit User' : 'Add New User'}</h2>
            <form onSubmit={handleSubmitUser}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={userForm.name}
                  onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                  placeholder="John Doe"
                  required
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  placeholder="john.doe@example.com"
                  required
                />
              </div>

              <div className="form-group">
                <label>Initials (optional)</label>
                <input
                  type="text"
                  value={userForm.initials}
                  onChange={(e) => setUserForm({ ...userForm, initials: e.target.value.substring(0, 3).toUpperCase() })}
                  placeholder="JD"
                  maxLength="3"
                />
                <small>Leave blank to auto-generate from name</small>
              </div>

              <div className="form-group">
                <label>Role *</label>
                <select
                  value={userForm.role}
                  onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                  required
                  className="role-select"
                >
                  <option value="Admin">Admin</option>
                  <option value="Officer">Officer</option>
                  <option value="Varsity">Varsity</option>
                  <option value="Club Novice">Club Novice</option>
                  <option value="Class Novice">Class Novice</option>
                </select>
                <small>Admin has full access. Officers/Varsity see Team Hub. Class Novice sees curriculum.</small>
              </div>

              <div className="form-group">
                <label>Permissions</label>
                <label className="permission-checkbox">
                  <input
                    type="checkbox"
                    checked={userForm.permissions.sessionHost}
                    onChange={(e) => setUserForm({
                      ...userForm,
                      permissions: { ...userForm.permissions, sessionHost: e.target.checked }
                    })}
                  />
                  <span>Session Host</span>
                </label>

                <label className="permission-checkbox">
                  <input
                    type="checkbox"
                    checked={userForm.permissions.pointTracking}
                    onChange={(e) => setUserForm({
                      ...userForm,
                      permissions: { ...userForm.permissions, pointTracking: e.target.checked }
                    })}
                  />
                  <span>Point Tracking</span>
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowUserModal(false)} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" className="submit-btn">
                  {editingUser ? 'Update User' : 'Add User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
